import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";

import vehicleDocumentsApi from "@/services/api/vehicleDocumentsApi";
import { useNotificationStore } from "@/stores/notificationStore";

export const useVehicleDocumentStore = defineStore("vehicleDocument", {
  state: () => ({
    companyId: getCompanyId(),
    documents: [],
    allDocuments: [],
    dataIsLoading: false,
    isSubmitting: false,
    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
      from: 0,
      to: 0,
    },

    documentKindOptions: [],
    ownershipTypeOptions: [],
    dataFieldOptions: {},
    optionsLoaded: false,
  }),

  getters: {
    documentKindLabels: (state) =>
      Object.fromEntries(
        state.documentKindOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    ownershipTypeLabels: (state) =>
      Object.fromEntries(
        state.ownershipTypeOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),
  },

  actions: {
    async ensureOptions() {
      if (this.optionsLoaded) return;

      const response = await vehicleDocumentsApi.options(this.companyId);
      const options = response.data.result || {};
      this.documentKindOptions = options.document_kind_options || [];
      this.ownershipTypeOptions = options.ownership_type_options || [];
      this.dataFieldOptions = options.data_field_options || {};
      this.optionsLoaded = true;
    },

    formatDocumentKind(value) {
      return this.documentKindLabels[value] || value || "—";
    },

    formatOwnershipType(value) {
      return this.ownershipTypeLabels[value] || value || "—";
    },

    async fetchDocuments(vehicleId) {
      this.dataIsLoading = true;

      try {
        await this.ensureOptions();
        const response = await vehicleDocumentsApi.index(
          this.companyId,
          vehicleId
        );
        this.documents = response.data.result || [];
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchAllDocuments(params = {}) {
      this.dataIsLoading = true;

      try {
        await this.ensureOptions();
        const response = await vehicleDocumentsApi.indexAll(
          this.companyId,
          params
        );
        this.allDocuments = response.data.result || [];

        const meta = response.data.pagination_meta;
        if (meta) {
          this.pagination.page = meta.current_page;
          this.pagination.pages = meta.total_pages;
          this.pagination.count = meta.count;
          this.pagination.from = meta.from;
          this.pagination.to = meta.to;
          this.pagination.series = meta.series || [];
        }
      } finally {
        this.dataIsLoading = false;
      }
    },

    async createDocument(vehicleId, payload) {
      this.isSubmitting = true;

      try {
        const response = await vehicleDocumentsApi.create(
          this.companyId,
          vehicleId,
          payload
        );
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС создан");
        return response.data.result;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateDocument(vehicleId, id, payload) {
      this.isSubmitting = true;

      try {
        const response = await vehicleDocumentsApi.update(
          this.companyId,
          vehicleId,
          id,
          payload
        );
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС обновлён");
        return response.data.result;
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteDocument(vehicleId, id) {
      this.isSubmitting = true;

      try {
        await vehicleDocumentsApi.destroy(this.companyId, vehicleId, id);
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС удалён");
      } finally {
        this.isSubmitting = false;
      }
    },

    async archiveDocument(vehicleId, id) {
      this.isSubmitting = true;

      try {
        await vehicleDocumentsApi.archive(this.companyId, vehicleId, id);
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС архивирован");
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreDocument(vehicleId, id) {
      this.isSubmitting = true;

      try {
        await vehicleDocumentsApi.restore(this.companyId, vehicleId, id);
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС восстановлен");
      } finally {
        this.isSubmitting = false;
      }
    },

    async setCurrent(vehicleId, id) {
      this.isSubmitting = true;

      try {
        const response = await vehicleDocumentsApi.setCurrent(
          this.companyId,
          vehicleId,
          id
        );
        await this.fetchDocuments(vehicleId);
        useNotificationStore().showSuccess("Документ ТС сделан текущим");
        return response.data.result;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
